import type { InvoiceLinkOut } from '@/api/invoices';
import type { FindingOut } from '@/api/types';

/** One transaction the collection email asks for — a To-validate
 *  finding with no paper attached. */
export interface MissingInvoiceRow {
  externalTransactionId: string;
  supplierName: string | null;
  billDate: string | null;
  billAmount: string | null;
  recoveryAtStake: number;
  ruleId: string;
  status: string;
}

export interface SupplierGroup {
  supplierName: string | null;
  rows: MissingInvoiceRow[];
  totalRecovery: number;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The collection worklist: dispositioned-toward-recovery findings
 *  (approved / needs_review — i.e. not pending
 *  and not rejected) whose transaction has NO attached document. One
 *  row per transaction — when several findings share a transaction,
 *  the highest recovery speaks for it (one invoice unblocks them all).
 */
export function missingInvoiceRows(
  findings: FindingOut[],
  linksByTransaction: Map<string, InvoiceLinkOut[]>,
  markedTransactions: Set<string> = new Set(),
): MissingInvoiceRow[] {
  const byTransaction = new Map<string, MissingInvoiceRow>();
  for (const f of findings) {
    const links = linksByTransaction.get(f.transaction_id);
    if (links && links.length > 0) continue;
    // Triaged "no invoice available" — stop asking for it.
    if (markedTransactions.has(f.transaction_id)) continue;
    if (f.status === 'pending' || f.status === 'rejected') continue;
    const recovery = Number(f.recovery.amount) || 0;
    const existing = byTransaction.get(f.transaction_id);
    if (existing && existing.recoveryAtStake >= recovery) continue;
    byTransaction.set(f.transaction_id, {
      externalTransactionId: f.transaction_id,
      supplierName: stringOrNull(f.evidence['supplier.name']),
      billDate: stringOrNull(f.evidence['bill_date']),
      billAmount: stringOrNull(f.evidence['bill_amount']),
      recoveryAtStake: recovery,
      ruleId: f.rule_id,
      status: f.status,
    });
  }
  return [...byTransaction.values()];
}

/** Grouped by supplier, biggest recovery first — inside and across
 *  groups — so the collection email leads with the money. */
export function groupBySupplier(rows: MissingInvoiceRow[]): SupplierGroup[] {
  const groups = new Map<string | null, SupplierGroup>();
  for (const row of rows) {
    const group = groups.get(row.supplierName) ?? {
      supplierName: row.supplierName,
      rows: [],
      totalRecovery: 0,
    };
    group.rows.push(row);
    group.totalRecovery += row.recoveryAtStake;
    groups.set(row.supplierName, group);
  }
  const sorted = [...groups.values()].sort((a, b) => b.totalRecovery - a.totalRecovery);
  for (const group of sorted) {
    group.rows.sort((a, b) => b.recoveryAtStake - a.recoveryAtStake);
  }
  return sorted;
}

/** CSV for the collection email / spreadsheet. Quotes every field —
 *  supplier names carry commas ("Norton Rose Fulbright Canada, LLP") —
 *  and neutralizes leading formula triggers (=, +, -, @, tab, CR) with
 *  an apostrophe: the file is explicitly built to be opened in
 *  Excel/Sheets, and supplier names originate from customer document
 *  data. */
export function missingInvoicesCsv(groups: SupplierGroup[]): string {
  const quote = (v: string | null) => {
    let s = (v ?? '').replaceAll('"', '""');
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return `"${s}"`;
  };
  const lines = [
    ['supplier', 'transaction_id', 'bill_date', 'bill_amount', 'recovery_at_stake']
      .map((h) => quote(h))
      .join(','),
  ];
  for (const group of groups) {
    for (const row of group.rows) {
      lines.push(
        [
          quote(row.supplierName),
          quote(row.externalTransactionId),
          quote(row.billDate),
          quote(row.billAmount),
          quote(row.recoveryAtStake.toFixed(2)),
        ].join(','),
      );
    }
  }
  return lines.join('\n');
}
